import "./index.scss";

import { Box, Button as MUIButton, ButtonGroup, Grid, Stack } from "@mui/material";
import { ReactComponent as IconAdd } from "assets/images/icons/add.svg";
import { ReactComponent as IconSortVertical } from "assets/images/icons/moving-vertical.svg";
import { ReactComponent as IconSelectorCornerNormal } from "assets/images/icons/selector-corner-normal.svg";
import { ReactComponent as IconSelectorCornerNormalHover } from "assets/images/icons/selector-corner-normal-hover.svg";
import { ReactComponent as IconSelectorCornerSelected } from "assets/images/icons/selector-corner-selected.svg";
import { ReactComponent as IconSelectorCornerSelectedHover } from "assets/images/icons/selector-corner-selected-hover.svg";
import { ReactComponent as IconTrash } from "assets/images/icons/trash.svg";
import Button from "components/Button";
import CustomBarChart from "components/CustomBarChart";
import DatePicker from "components/DatePicker";
import Input from "components/Input";
import RatingBar from "components/RatingBar";
import ScreenEmptySection from "components/ScreenEmptySection";
import {
  DATE_FORMAT,
  MSG_ENTRY_SAVED_SUCCESSFULLY,
  MYSQL_DATE_FORMAT,
  MYSQL_DATE_FORMAT_LONG,
} from "constants/index";
import { TPGPLongTermGoal } from "models/tca";
import moment from "moment";
import { nanoid } from "nanoid";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { toast } from "react-toastify";

const PGP = forwardRef(
  (
    props: {
      previousGoals?: { [key: string]: string | number }[];
      goals: TPGPLongTermGoal[];
      appointmentNumber: number;
      currentTab: string;
      shareSubView?: Function;
    },
    ref
  ) => {
    const { previousGoals, goals, appointmentNumber, currentTab, shareSubView } = props;
    const [showGraph, setShowGraph] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState<TPGPLongTermGoal>();
    const [goalsList, setGoalsList] = useState<TPGPLongTermGoal[]>([]);
    const formContext = useFormContext();
    const { setValue, getValues } = formContext;
    const [graphData, setGraphData] = useState<any[]>([]);
    const [graphColumns, setGraphColumns] = useState<any[]>([]);
    const formLongTermGoalField = useRef(null);

    //#region Set next appointment date based on current appointment number (initial appointment schedules next in 2 weeks, 1st and 2nd => 4 weeks otherwise 8 weeks)
    let nextAppointmentNbOfWeeks = 2;
    switch (appointmentNumber) {
      case 1:
      case 2:
        nextAppointmentNbOfWeeks = 4;
        break;
      case 3:
      case 4:
        nextAppointmentNbOfWeeks = 8;
        break;
      default:
        break;
    }
    const nextAppointmentDate = moment().add(nextAppointmentNbOfWeeks, "w").format(DATE_FORMAT);
    const nextAppointmentDateForDb = moment()
      .add(nextAppointmentNbOfWeeks, "w")
      .format(MYSQL_DATE_FORMAT);
    //#endregion Set next appointment date based on current appointment number (initial appointment schedules next in 2 weeks, 1st and 2nd => 4 weeks otherwise 8 weeks)

    //#region short term variable ref controls
    const shortTerm1FromRef = useRef(null);
    const shortTerm1ToRef = useRef(null);

    const shortTerm2FromRef = useRef(null);
    const shortTerm2ToRef = useRef(null);

    const fromFieldReferences = [shortTerm1FromRef, shortTerm2FromRef];
    const toFieldReferences = [shortTerm1ToRef, shortTerm2ToRef];
    //#endregion short term variable ref controls

    const [longTermGoalCount, setLongTermGoalCount] = useState(0);
    const longTermGoalLength: number = 50;

    const [shortTermGoal0FromCount, setShortTermGoal0FromCount] = useState(0);
    const [shortTermGoal0ToCount, setShortTermGoal0ToCount] = useState(0);

    const [shortTermGoal1FromCount, setShortTermGoal1FromCount] = useState(0);
    const [shortTermGoal1ToCount, setShortTermGoal1ToCount] = useState(0);

    const [shortTermGoal2FromCount, setShortTermGoal2FromCount] = useState(0);
    const [shortTermGoal2ToCount, setShortTermGoal2ToCount] = useState(0);

    const shortTermGoalLength = 100;

    useEffect(() => {
      let tempGoals = goals?.map((m) => {
        if (!m.id) m.id = nanoid();
        if (!m.haveNullifiedRatingFarFromGoal) {
          m.haveNullifiedRatingFarFromGoal = true;
          m.ratingFarFromGoal = null;
        }
        return m;
      });

      //#region Updating selected goal
      if (
        tempGoals?.length &&
        (!selectedGoal || !tempGoals.find((f) => f.id === selectedGoal.id))
      ) {
        setSelectedGoal(bufferShortTermGoals({ ...tempGoals[0] }));
      }
      if (!tempGoals?.length) {
        setSelectedGoal(null);
        addPgp();
      }
      //#endregion Updating selected goal

      //updating goals list
      setGoalsList(tempGoals || []);

      //#region updating graphData
      let tempGraphColumns = [];
      let copyOfPreviousGoals = [...(previousGoals || [])];

      if (tempGoals?.length) {
        tempGoals.forEach((fe, i) => {
          if (i + 1 > copyOfPreviousGoals.length) {
            copyOfPreviousGoals.push({ goal: `Goal ${i + 1}` });
          }

          copyOfPreviousGoals[i]["goal"] = `Goal ${i + 1}: ` + fe.longTermGoal;
          copyOfPreviousGoals[i][`Goal${i + 1}_${moment().format(DATE_FORMAT)}`] =
            fe.ratingFarFromGoal;
        });
      }

      //#region Get columns from previous goals
      copyOfPreviousGoals?.forEach((previousGoal, i) => {
        let columnColor = "";
        if (`${previousGoal["goal"]}`.indexOf("Goal 1") !== -1) {
          columnColor = "#4646ff";
        }
        if (`${previousGoal["goal"]}`.indexOf("Goal 2") !== -1) {
          columnColor = "#00c400";
        }
        if (`${previousGoal["goal"]}`.indexOf("Goal 3") !== -1) {
          columnColor = "#ff4646";
        }
        Object.keys(previousGoal).forEach((key) => {
          if (key === "goal") return;
          tempGraphColumns.push({
            color: columnColor,
            label: key.replace(`Goal${i + 1}_`, ""),
            name: key,
          });
          //Check if the key already exists in columns
          // if (!tempGraphColumns.find((f) => f.name === key)) {
          // }
        });
      });
      //#endregion Get columns from previous goals

      setGraphData([...(copyOfPreviousGoals || [])]);
      setGraphColumns(tempGraphColumns);
      //#endregion updating graphData
    }, [goals, previousGoals]);

    useEffect(() => {
      if (currentTab === "pgp" && !showGraph) formLongTermGoalField?.current?.focus();
    }, [currentTab, showGraph]);

    useImperativeHandle(ref, () => ({
      switchView(view: string) {
        setShowGraph(view === "graph");
      },

      validate() {
        return validate();
      },
    }));

    //#region functions
    const addPgp = () => {
      if (goalsList.length === 3) {
        toast("There can only be up to 3 long term goals.", { type: "error" });
        return;
      }
      setSelectedGoal({
        haveNullifiedRatingFarFromGoal: true,
        id: nanoid(),
        longTermGoal: "",
        ratingFarFromGoal: null,
        shortTermGoals: [
          {
            shortTermGoalsBy: nextAppointmentDateForDb,
            whereIAmNow: "",
            whereIWantToBe: "",
          },
          {
            shortTermGoalsBy: nextAppointmentDateForDb,
            whereIAmNow: "",
            whereIWantToBe: "",
          },
          {
            shortTermGoalsBy: nextAppointmentDateForDb,
            whereIAmNow: "",
            whereIWantToBe: "",
          },
        ],
        timestamp: moment().format(DATE_FORMAT),
      });
      formLongTermGoalField?.current?.focus();
    };

    const cleanupShortTermGoals = (goal: TPGPLongTermGoal) => {
      //remove empty entries
      goal.shortTermGoals = goal.shortTermGoals.filter((f) => f.whereIAmNow && f.whereIWantToBe);
      return goal;
    };

    const bufferShortTermGoals = (goal: TPGPLongTermGoal) => {
      if (!goal.shortTermGoals) {
        goal.shortTermGoals = [];
      }
      for (let i = goal.shortTermGoals.length; i < 3; i++) {
        goal.shortTermGoals.push({
          shortTermGoalsBy: nextAppointmentDateForDb,
          whereIAmNow: "",
          whereIWantToBe: "",
        });
      }
      return goal;
    };

    const renderLegend = () => {
      return (
        <Stack direction="row" justifyContent="left" alignItems="center" ml={5} mt={1} gap={1}>
          {graphData?.map((m, index) => {
            let columnColor = "";
            switch (index) {
              case 0:
                columnColor = "#4646ff";
                break;
              case 1:
                columnColor = "#00c400";
                break;
              case 2:
                columnColor = "#ff4646";
                break;
            }

            return (
              <Stack
                key={`pgp_legend_${index}`}
                direction="row"
                justifyContent="left"
                gap={1}
                alignItems="center"
              >
                <Box className="legend-circle-view" bgcolor={columnColor}></Box>
                {m.goal}
              </Stack>
            );
          })}
        </Stack>
      );
    };

    const validate = () => {
      //Returns 0 if we need to alert the person he should add at least one goal
      //Returns -1 if _loaded_ goals are missing rating
      //If valid, return 1
      if (goalsList.length === 0) return 0;
      let isValid = 1;
      goalsList.forEach((g) => {
        if (!g.ratingFarFromGoal) {
          toast(`Goal "${g.longTermGoal}" is missing its rating`, { type: "error" });
          isValid = -1;
        }
      });
      return isValid;
    };
    //#endregion functions

    //#region event handlers
    const handleSelectGoal = (g: TPGPLongTermGoal) => {
      let goalCopy = bufferShortTermGoals({ ...g });
      setSelectedGoal(goalCopy);
    };

    const handleRemoveGoal = (id: string) => {
      const newGoalsList = [...goalsList.filter((f) => f.id !== id)];
      setValue("pgp", newGoalsList);
    };

    const handleSaveGoal = () => {
      if (!getValues("nextAppointmentDate")) {
        setValue(
          "nextAppointmentDate",
          moment().add(nextAppointmentNbOfWeeks, "w").format(MYSQL_DATE_FORMAT_LONG)
        );
      }
      if (!selectedGoal.longTermGoal || !selectedGoal.ratingFarFromGoal) {
        toast("Long term name and progress must be filled.", { type: "error" });
        return;
      }
      let pgpGoals = [...goalsList];
      let tempSelectedGoal = cleanupShortTermGoals({ ...selectedGoal });

      let existingEntry = pgpGoals.findIndex((f) => f.id === tempSelectedGoal.id);
      //If the object exists in the table update it
      if (existingEntry !== -1) {
        pgpGoals[existingEntry] = { ...tempSelectedGoal };
      } else {
        //If the object doesn't exist in the table add it
        pgpGoals.push({ ...tempSelectedGoal });
      }
      setValue("pgp", pgpGoals);
      toast(MSG_ENTRY_SAVED_SUCCESSFULLY, { type: "success" });
    };

    const handleFromFieldFocusShift = (index: number) => {
      if (index > 0 && !selectedGoal.shortTermGoals[index - 1].whereIAmNow) {
        fromFieldReferences[index - 1].current.focus();
      }
    };

    const handleToFieldFocusShift = (index: number) => {
      if (index > 0 && !selectedGoal.shortTermGoals[index - 1].whereIWantToBe) {
        toFieldReferences[index - 1].current.focus();
      }
    };

    const handleUpdateShortTermByDate = (v: string) => {
      let selectedGoalCopy = { ...selectedGoal };

      setValue("nextAppointmentDate", moment(v).format(MYSQL_DATE_FORMAT_LONG));
      selectedGoalCopy.nextAppointmentDate = moment(v).format(MYSQL_DATE_FORMAT_LONG);
      selectedGoalCopy.shortTermGoals = selectedGoalCopy.shortTermGoals.map((m) => {
        m.shortTermGoalsBy = moment(v).format(MYSQL_DATE_FORMAT_LONG);
        return m;
      });
      setSelectedGoal({ ...selectedGoalCopy });
    };

    const handleUpdateShortTermEntryFrom = (e, index: number) => {
      let selectedGoalCopy = { ...selectedGoal };
      selectedGoalCopy.shortTermGoals[index].whereIAmNow = e.target.value;
      setSelectedGoal({ ...selectedGoalCopy });
    };

    const handleUpdateShortTermEntryTo = (e, index: number) => {
      let selectedGoalCopy = { ...selectedGoal };
      selectedGoalCopy.shortTermGoals[index].whereIWantToBe = e.target.value;
      setSelectedGoal({ ...selectedGoalCopy });
    };

    const handleToggleShowGraph = () => {
      const validationResult = validate();
      if (validationResult <= 0) {
        return;
      }
      if (shareSubView) {
        shareSubView(!showGraph);
      }
      setShowGraph(!showGraph);
    };
    //#endregion event handlers

    if (currentTab !== "pgp") return null;
    return (
      <Stack className="tcaPgp" flexGrow={1}>
        <Box width={`374px`} mb={`20px`}>
          <ButtonGroup className="button-group">
            <MUIButton className={`${!showGraph ? `active` : ``}`} onClick={handleToggleShowGraph}>
              PGP Assessment
            </MUIButton>
            <MUIButton className={`${showGraph ? `active` : ``}`} onClick={handleToggleShowGraph}>
              PGP Graph
            </MUIButton>
          </ButtonGroup>
        </Box>
        {!showGraph && (
          <>
            {appointmentNumber === 0 && (
              <h3>
                Goal setting is a very important component in exercise adherence. Over the course of
                our time together, we'll identify your long-term goal(s) as well as set short-term
                goals at each appointment.
                <br />
                <br />
                Let's start by identifying your long-term goal(s). Please be as specific as
                possible. Typically, long-term goals will be focused on: <br />
                1) weight-loss <br />
                2) day-to-day functioning or <br />
                3) accomplishing a physical goal such as walking/running a 5K.
                <br />
                <br />
                If there are other areas of importance, we can include those also. After identifying
                your long-term goal(s), please use the 1-10 scale to rate how far you are from
                attaining each goal.
              </h3>
            )}
            {appointmentNumber !== 0 && (
              <h3>
                Below are the long-term goal(s) you identified at our initial appointment concerning
                your exercise program. Today, for each long-term goal, we will determine the
                following:
                <br />
                <br />
                1) Is this still your long-term goal? <br />
                2) On a scale from 1 - 10, how far do you feel that you are from attaining that
                goal? <br />
                3) What are the short-term process goals that will help you reach your long-term
                goal?
                <br />
              </h3>
            )}
            <Box className="primary-details-section program-exercise-grid" mt={`30px`}>
              <Box className="inner-container program-selection-container">
                <Grid container flexGrow={1}>
                  <Grid item xs={4}>
                    <Grid container>
                      <Grid item xs={11} className="header" pl="12px">
                        Long Term Goals
                      </Grid>
                      <Grid item xs={1} className="header" justifyContent={"flex-end"}>
                        <button
                          type="button"
                          className="edit-icon"
                          onClick={() => {
                            addPgp();
                          }}
                          title="Add"
                        >
                          <IconAdd />
                        </button>
                      </Grid>
                      {goalsList && (
                        <ReactSortable
                          list={goalsList}
                          setList={(v) => {
                            if (v.length) setValue("pgp", v);
                          }}
                          animation={200}
                          className="reactSortableFullWidth"
                        >
                          {goalsList.map((e, index) => (
                            <Grid
                              container
                              className={`program-entry ${
                                e.id === selectedGoal?.id ? `selected` : ``
                              }`}
                              alignItems={"center"}
                              onClick={() => handleSelectGoal(e)}
                              key={`program_${index}`}
                            >
                              <Grid item xs={10}>
                                <IconSortVertical className="sort-handle" />
                                {e.longTermGoal}
                              </Grid>
                              <Grid item xs={1} flexDirection="row-reverse">
                                <IconTrash onClick={() => handleRemoveGoal(e.id)} />
                              </Grid>
                              <Grid item xs={1}>
                                <IconSelectorCornerNormal className="showNormal" />
                                <IconSelectorCornerNormalHover className="showNormalHover" />
                                <IconSelectorCornerSelected className="showSelected" />
                                <IconSelectorCornerSelectedHover className="showSelectedHover" />
                              </Grid>
                            </Grid>
                          ))}
                        </ReactSortable>
                      )}
                      {!goals && <ScreenEmptySection />}
                    </Grid>
                  </Grid>
                  <Grid item xs={8}>
                    <Grid container>
                      <Grid item xs={12} className="header" pl="12px">
                        Add/Modify Goal
                      </Grid>
                      {selectedGoal && (
                        <Stack className="longTermGoalForm" pl={"12px"} alignItems={"right"}>
                          {/* Long Term Goal */}
                          <Box my={"10px"}>Long Term Goal</Box>
                          <Input
                            value={selectedGoal.longTermGoal}
                            onChange={(v) => {
                              setSelectedGoal({ ...selectedGoal, longTermGoal: v.target.value });
                              setLongTermGoalCount(v.target.value.length);
                            }}
                            ref={formLongTermGoalField}
                            maxLength={longTermGoalLength}
                          />
                          <Stack
                            direction={"row"}
                            alignItems={"right"}
                            justifyContent={"flex-end"}
                            gap={1}
                          >
                            {longTermGoalCount}/{longTermGoalLength}
                          </Stack>

                          {/* Far From Goal */}
                          <Stack>
                            <Stack direction={"row"} justifyContent={"space-between"} my={"10px"}>
                              <Box>Far from Goal</Box>
                              <Box>Goal is Attained</Box>
                            </Stack>
                            <RatingBar
                              numberOfButtons={10}
                              onChange={(v) =>
                                setSelectedGoal({ ...selectedGoal, ratingFarFromGoal: v })
                              }
                              value={selectedGoal.ratingFarFromGoal}
                            />
                            <Box className="hint" my={"10px"}>
                              The next follow up appointment should take place on or around{" "}
                              {nextAppointmentDate}
                              {appointmentNumber !== 0 && (
                                <>
                                  , all short-term goals should be attained by the next appointment.
                                </>
                              )}
                            </Box>
                            <Grid container spacing={1}>
                              <Grid item xs={6} alignItems="center">
                                <Stack
                                  justifyContent={"center"}
                                  direction={"column"}
                                  height={"100%"}
                                >
                                  <Box>Where I Am Now</Box>
                                </Stack>
                              </Grid>
                              <Grid item xs={6}>
                                <Stack direction={"row"} alignItems="center" gap={1}>
                                  <Box flexShrink={0}>Short Term Goal(s) by:</Box>
                                  <DatePicker
                                    className="base-dropdown"
                                    fullWidth={true}
                                    value={nextAppointmentDateForDb}
                                    onChange={(v) => handleUpdateShortTermByDate(v)}
                                    minDate={new Date()}
                                    disabled={appointmentNumber === 0}
                                  />
                                </Stack>
                              </Grid>

                              {/* Short Term Goal From 1 (0)*/}
                              <Grid item xs={6}>
                                <Stack direction={"column"} alignItems={"right"} gap={1}>
                                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                                    <Box>1</Box>
                                    <Box className="base-input">
                                      <input
                                        ref={shortTerm1FromRef}
                                        value={selectedGoal.shortTermGoals[0].whereIAmNow}
                                        onChange={(e) => {
                                          handleUpdateShortTermEntryFrom(e, 0);
                                          setShortTermGoal0FromCount(e.target.value.length);
                                        }}
                                        maxLength={shortTermGoalLength}
                                        disabled={appointmentNumber === 0}
                                      />
                                    </Box>
                                  </Stack>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"right"}
                                    justifyContent={"flex-end"}
                                    gap={1}
                                  >
                                    {shortTermGoal0FromCount}/{shortTermGoalLength}
                                  </Stack>
                                </Stack>
                              </Grid>

                              {/* Short Term Goal To 1 (0)*/}
                              <Grid item xs={6}>
                                <Stack
                                  direction={"column"}
                                  alignItems={"left"}
                                  gap={1}
                                  justifyContent={"flex-end"}
                                >
                                  <Box className="base-input">
                                    <input
                                      ref={shortTerm1ToRef}
                                      value={selectedGoal.shortTermGoals[0].whereIWantToBe}
                                      onChange={(e) => {
                                        handleUpdateShortTermEntryTo(e, 0);
                                        setShortTermGoal0ToCount(e.target.value.length);
                                      }}
                                      maxLength={shortTermGoalLength}
                                      disabled={appointmentNumber === 0}
                                    />
                                  </Box>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"right"}
                                    justifyContent={"flex-end"}
                                    gap={1}
                                  >
                                    {shortTermGoal0ToCount}/{shortTermGoalLength}
                                  </Stack>
                                </Stack>
                              </Grid>

                              {/* Short Term Goal From 2 (1)*/}
                              <Grid item xs={6}>
                                <Stack direction={"column"} alignItems={"right"} gap={1}>
                                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                                    <Box>2</Box>
                                    <Box className="base-input">
                                      <input
                                        ref={shortTerm2FromRef}
                                        value={selectedGoal.shortTermGoals[1].whereIAmNow}
                                        onChange={(e) => {
                                          handleUpdateShortTermEntryFrom(e, 1);
                                          setShortTermGoal1FromCount(e.target.value.length);
                                        }}
                                        maxLength={shortTermGoalLength}
                                        disabled={appointmentNumber === 0}
                                        onFocus={() => handleFromFieldFocusShift(1)}
                                      />
                                    </Box>
                                  </Stack>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"right"}
                                    justifyContent={"flex-end"}
                                    gap={1}
                                  >
                                    {shortTermGoal1FromCount}/{shortTermGoalLength}
                                  </Stack>
                                </Stack>
                              </Grid>

                              {/* Short Term Goal To 2 (1)*/}
                              <Grid item xs={6}>
                                <Box className="base-input">
                                  <input
                                    ref={shortTerm2ToRef}
                                    value={selectedGoal.shortTermGoals[1].whereIWantToBe}
                                    onChange={(e) => {
                                      handleUpdateShortTermEntryTo(e, 1);
                                      setShortTermGoal1ToCount(e.target.value.length);
                                    }}
                                    maxLength={shortTermGoalLength}
                                    disabled={appointmentNumber === 0}
                                    onFocus={() => handleToFieldFocusShift(1)}
                                  />
                                </Box>
                                <Stack
                                  direction={"row"}
                                  alignItems={"right"}
                                  justifyContent={"flex-end"}
                                  gap={1}
                                >
                                  {shortTermGoal1ToCount}/{shortTermGoalLength}
                                </Stack>
                              </Grid>

                              {/* Short Term Goal From 3 (2)*/}
                              <Grid item xs={6}>
                                <Stack direction={"column"} alignItems={"right"} gap={1}>
                                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                                    <Box>3</Box>
                                    <Box className="base-input">
                                      <input
                                        value={selectedGoal.shortTermGoals[2].whereIAmNow}
                                        onChange={(e) => {
                                          handleUpdateShortTermEntryFrom(e, 2);
                                          setShortTermGoal2FromCount(e.target.value.length);
                                        }}
                                        maxLength={shortTermGoalLength}
                                        disabled={appointmentNumber === 0}
                                        onFocus={() => handleFromFieldFocusShift(2)}
                                      />
                                    </Box>
                                  </Stack>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"right"}
                                    justifyContent={"flex-end"}
                                    gap={1}
                                  >
                                    {shortTermGoal2FromCount}/{shortTermGoalLength}
                                  </Stack>
                                </Stack>
                              </Grid>

                              {/* Short Term Goal To 3 (2)*/}
                              <Grid item xs={6}>
                                <Box className="base-input">
                                  <input
                                    value={selectedGoal.shortTermGoals[2].whereIWantToBe}
                                    onChange={(e) => {
                                      handleUpdateShortTermEntryTo(e, 2);
                                      setShortTermGoal2ToCount(e.target.value.length);
                                    }}
                                    maxLength={shortTermGoalLength}
                                    disabled={appointmentNumber === 0}
                                    onFocus={() => handleToFieldFocusShift(2)}
                                  />
                                </Box>
                                <Stack
                                  direction={"row"}
                                  alignItems={"right"}
                                  justifyContent={"flex-end"}
                                  gap={1}
                                >
                                  {shortTermGoal2ToCount}/{shortTermGoalLength}
                                </Stack>
                              </Grid>

                              <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end">
                                  <Button
                                    label="Save Goal"
                                    className="confirm-button"
                                    type="button"
                                    onClick={handleSaveGoal}
                                  />
                                </Stack>
                              </Grid>
                            </Grid>
                          </Stack>
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </>
        )}
        {showGraph && (
          <Box height={"400px"}>
            <CustomBarChart
              data={graphData}
              xAxisDataKey="goal"
              bars={graphColumns}
              legendRenderCallback={() => renderLegend()}
            />
          </Box>
        )}
      </Stack>
    );
  }
);

export default PGP;
